/**
 * What every page inside a world shares.
 *
 * `CataloguePage` is the only place a world's own states are decided:
 * loading, a failed request, a world that does not exist, and a world that
 * exists with none of its books published yet. Each page renders only once
 * there is something to show, so none of them can fall through to an empty
 * list — which would look exactly like a bug, on the one day nobody is
 * watching.
 */

import { useEffect, type ReactNode } from 'react'
import { Link } from 'react-router'
import type { CatalogueSeries } from '../../shared/catalogue.ts'
import { worldPath } from '../../shared/routes.ts'
import { useI18n } from '../i18n/index.tsx'
import { fill, seriesDescription, seriesName, ungroupedLabel, type Loaded } from '../lib/catalogue.ts'
import { useWorld } from '../lib/world.tsx'
import { CompassCorner } from './Ornament.tsx'

/** The tab title, set client-side. Crawlers may not see it; see issue #49. */
export function usePageTitle(title: string | null): void {
  const { t } = useI18n()
  useEffect(() => {
    document.title = title === null ? t('app.name') : `${title} — ${t('app.name')}`
  }, [title, t])
}

export function CataloguePage({ children }: { children: (catalogue: Loaded, world: string) => ReactNode }) {
  const { t } = useI18n()
  const { slug, state } = useWorld()

  if (state.status === 'loading') {
    return (
      <section className="panel">
        <p>{t('app.loading')}</p>
      </section>
    )
  }
  if (state.status === 'error') {
    return (
      <section className="panel">
        <p className="alert alert--error">{t('catalogue.error')}</p>
      </section>
    )
  }
  if (state.status === 'not-found') return <WorldNotFound />
  if (state.status === 'not-loaded') {
    return (
      <section className="panel">
        <h1>{t('catalogue.not_loaded.title')}</h1>
        <p>{t('catalogue.not_loaded.body')}</p>
      </section>
    )
  }
  return <>{children(state, slug)}</>
}

/** No such world, or not published. The Worker has already answered 404. */
export function WorldNotFound() {
  const { t } = useI18n()
  usePageTitle(t('world.not_found.title'))
  return (
    <section className="panel">
      <h1>{t('world.not_found.title')}</h1>
      <p>
        {t('world.not_found.body')} <Link to="/">{t('world.all')}</Link>
      </p>
    </section>
  )
}

/** „1 книга“, „4 книги“. */
export function useBookCount(): (count: number) => string {
  const { t, locale } = useI18n()
  const rules = new Intl.PluralRules(locale === 'bg' ? 'bg-BG' : 'en')
  return (count) => fill(t(rules.select(count) === 'one' ? 'books.count.one' : 'books.count.other'), { count })
}

/** „1 спирка“, „7 спирки“: a path's books counted as its stops. */
export function useStops(): (count: number) => string {
  const { t, locale } = useI18n()
  const rules = new Intl.PluralRules(locale === 'bg' ? 'bg-BG' : 'en')
  return (count) => fill(t(rules.select(count) === 'one' ? 'series.stops.one' : 'series.stops.other'), { count })
}

/**
 * A path this short is not really a route: two stand-alone books, say. It goes
 * in the wide row at the foot of the grid with „Извън поредиците“, as on the
 * approved board, rather than taking a full card in the grid.
 */
const SHORT_PATH = 2

/**
 * The series to choose from: a grid of cards, three across on a wide screen,
 * then a row of wide cards for the short paths and the works outside every
 * path. Each card is short on purpose — its description is clamped to three
 * lines by the stylesheet, and the whole of it is on the series page.
 */
export function SeriesCards({ catalogue, world, headingLevel }: { catalogue: Loaded; world: string; headingLevel: 2 | 3 }) {
  const { t, locale } = useI18n()
  const stops = useStops()
  const books = useBookCount()
  const Heading = headingLevel === 2 ? 'h2' : 'h3'
  const outsideLabel = ungroupedLabel(catalogue.universe, locale)
  const outside = catalogue.works.filter((work) => work.series.length === 0)
  const grid = catalogue.series.filter((series) => series.books.length > SHORT_PATH)
  const short = catalogue.series.filter((series) => series.books.length <= SHORT_PATH)

  return (
    <div className="series-choice">
      <div className="series-grid">
        {grid.map((series: CatalogueSeries) => {
          const description = seriesDescription(series, locale)
          return (
            <article key={series.slug} className="series-card">
              <CompassCorner />
              <Heading className="series-card__title">
                <Link to={worldPath(world, 'series', series.slug)}>{seriesName(series, locale)}</Link>
              </Heading>
              <p className="label">{stops(series.books.length)}</p>
              {description === null ? null : <p className="series-card__text">{description}</p>}
            </article>
          )
        })}
      </div>
      {short.length === 0 && (outside.length === 0 || outsideLabel === null) ? null : (
        <div className="series-wide">
          {short.map((series) => (
            <article key={series.slug} className="wide-card">
              <div>
                <Heading className="wide-card__title">
                  <Link to={worldPath(world, 'series', series.slug)}>{seriesName(series, locale)}</Link>
                </Heading>
                <p className="wide-card__meta">{stops(series.books.length)}</p>
              </div>
              <span className="wide-card__chevron" aria-hidden="true">
                ›
              </span>
            </article>
          ))}
          {outside.length === 0 || outsideLabel === null ? null : (
            <article className="wide-card wide-card--outside">
              <div>
                <Heading className="wide-card__title">
                  <Link to={worldPath(world, 'outside')}>{outsideLabel}</Link>
                </Heading>
                <p className="wide-card__meta">{fill(t('outside.meta'), { books: books(outside.length) })}</p>
              </div>
              <span className="wide-card__chevron" aria-hidden="true">
                ›
              </span>
            </article>
          )}
        </div>
      )}
    </div>
  )
}
